import type { TScriptDynamicResource } from '@zep/proto';

import { Direction } from './direction';

export type EquipmentDirection = Direction;

/**
 * Directional sprite prefixes shared by the game renderer and avatar UI.
 * Keeping the string convention here prevents cosmetic-only pet renderers
 * from drifting away from the world renderer's asset contract.
 */
export type PetAnimationDirection = 'down' | 'left' | 'right' | 'up';

export type PetAnimationBehavior = 'idle' | 'walk' | 'jump' | 'sit' | 'dance';

export type PetAnimationState = {
  /** The owner is in the visual jump window. */
  ownerIsJumping: boolean;
  /** The follower is still consuming a safe route edge. */
  followerIsMoving: boolean;
  /** The owner is in the replicated sit action state. */
  ownerIsSitting: boolean;
  /** The owner is in the replicated dance action state. */
  ownerIsDancing: boolean;
};

export type ResolvedEquipmentDirectionState = {
  zIndex: number;
  offsetX: number;
  offsetY: number;
  /** Undefined when the catalog did not explicitly declare a value. */
  flipX?: boolean;
  /** Undefined when the catalog did not explicitly declare a value. */
  visible?: boolean;
};

const uniqueAnimationCandidates = (candidates: readonly string[]) => [
  ...new Set(candidates),
];

const createPetAnimationCandidates = (direction: PetAnimationDirection) => ({
  idle: uniqueAnimationCandidates([
    `${direction}_idle`,
    'idle',
    direction,
    'down_idle',
    'down',
    'default',
  ]),
  walk: uniqueAnimationCandidates([
    `${direction}_walk`,
    direction,
    'walk',
    'down_walk',
    'down',
    'down_idle',
    'idle',
    'default',
  ]),
  jump: uniqueAnimationCandidates([
    `${direction}_jump`,
    'jump',
    'down_jump',
    `${direction}_idle`,
    'idle',
    direction,
    'down_idle',
    'down',
    'default',
  ]),
  sit: uniqueAnimationCandidates([
    `${direction}_sit`,
    'sit',
    'down_sit',
    `${direction}_idle`,
    'idle',
    direction,
    'down_idle',
    'down',
    'default',
  ]),
  dance: uniqueAnimationCandidates([
    'dance',
    `${direction}_dance`,
    'down_dance',
    `${direction}_idle`,
    'idle',
    direction,
    'down_idle',
    'down',
    'default',
  ]),
});

const PET_ANIMATION_CANDIDATES: Record<
  PetAnimationDirection,
  Record<PetAnimationBehavior, readonly string[]>
> = {
  down: createPetAnimationCandidates('down'),
  left: createPetAnimationCandidates('left'),
  right: createPetAnimationCandidates('right'),
  up: createPetAnimationCandidates('up'),
};

const PET_THUMBNAIL_ANIMATION_CANDIDATES = uniqueAnimationCandidates([
  'down_idle',
  'down',
  'left_idle',
  'left',
  'right_idle',
  'right',
  'up_idle',
  'up',
  'idle',
  'default',
]);

/**
 * Collapses diagonal directions to the same horizontal cardinal used by the
 * player animation loop and equipment layer cascade.
 */
export function normalizeEquipmentDirection(direction: Direction): Direction {
  switch (direction) {
    case Direction.LEFT_UP:
    case Direction.LEFT_DOWN:
      return Direction.LEFT;
    case Direction.RIGHT_UP:
    case Direction.RIGHT_DOWN:
      return Direction.RIGHT;
    // proto enum 미설정값이 0(=NONE)이라 원격 플레이어는 방향 없이 join 할 수 있다.
    // NONE 을 흘려보내면 방향별 룩업이 전부 빗나가 장착물이 통째로 사라진다 —
    // 쉬는 자세의 정본인 DOWN 으로 접는다.
    case Direction.NONE:
      return Direction.DOWN;
    default:
      return direction;
  }
}

export function getPetAnimationDirection(
  direction: Direction,
): PetAnimationDirection {
  switch (normalizeEquipmentDirection(direction)) {
    case Direction.LEFT:
      return 'left';
    case Direction.RIGHT:
      return 'right';
    case Direction.UP:
      return 'up';
    default:
      return 'down';
  }
}

/**
 * Selects the pet presentation in the same order as the owner: jump is
 * immediate, route movement wins while the pet catches up, and owner actions
 * begin once the companion has settled on a safe tile.
 */
export function resolvePetAnimationBehavior({
  ownerIsJumping,
  followerIsMoving,
  ownerIsSitting,
  ownerIsDancing,
}: PetAnimationState): PetAnimationBehavior {
  if (ownerIsJumping) return 'jump';
  if (followerIsMoving) return 'walk';
  if (ownerIsSitting) return 'sit';
  return ownerIsDancing ? 'dance' : 'idle';
}

export function getPetAnimationCandidates(
  direction: Direction,
  behavior: PetAnimationBehavior,
): readonly string[] {
  return PET_ANIMATION_CANDIDATES[getPetAnimationDirection(direction)][
    behavior
  ];
}

/** Neutral-idle candidate order for equipment thumbnails. */
export function getPetThumbnailAnimationCandidates(): readonly string[] {
  return PET_THUMBNAIL_ANIMATION_CANDIDATES;
}

export function findPetAnimationCandidate(
  direction: Direction,
  behavior: PetAnimationBehavior,
  hasAnimation: (candidate: string) => boolean,
): string | undefined {
  return getPetAnimationCandidates(direction, behavior).find(hasAnimation);
}

export function resolvePetAnimationKey(
  anims: Record<string, unknown> | null | undefined,
  direction: Direction,
  behavior: PetAnimationBehavior,
): string | undefined {
  if (!anims) return undefined;

  return (
    findPetAnimationCandidate(direction, behavior, candidate =>
      Boolean(anims[candidate]),
    ) ?? Object.keys(anims)[0]
  );
}

/** Whether a resolved animation is a real jump/dance action rather than idle fallback. */
export function isPetActionAnimation(
  animationKey: string | undefined,
  behavior: Extract<PetAnimationBehavior, 'jump' | 'dance'>,
): boolean {
  if (!animationKey) return false;
  return animationKey === behavior || animationKey.endsWith(`_${behavior}`);
}

export interface DirectionState {
  zIndex?: number;
  offsetX?: number;
  offsetY?: number;
  flipX?: boolean;
  visible?: boolean;
}

export interface EquipmentLayer {
  resource: TScriptDynamicResource;
  default: DirectionState;
  perDirection?: Partial<Record<EquipmentDirection, Partial<DirectionState>>>;
}

/**
 * Resolves the catalog's sparse direction overlay without coercing optional
 * flip/visibility flags. World rendering can therefore preserve the same
 * "unset means leave the sprite alone" behavior as baked equipment state.
 */
export function resolveEquipmentLayerState(
  layer: EquipmentLayer,
  direction: Direction,
): ResolvedEquipmentDirectionState {
  const override = layer.perDirection?.[normalizeEquipmentDirection(direction)];

  return {
    zIndex: override?.zIndex ?? layer.default.zIndex ?? 0,
    offsetX: override?.offsetX ?? layer.default.offsetX ?? 0,
    offsetY: override?.offsetY ?? layer.default.offsetY ?? 0,
    flipX: override?.flipX ?? layer.default.flipX,
    visible: override?.visible ?? layer.default.visible,
  };
}

export interface BaseEquipmentItem {
  id: number;
  sku: string;
  name: string;
  thumbnailPath: string;
  layers: EquipmentLayer[];
  acquisitionMode: 'default' | 'purchase';
  priceZem: number | null;
  price: number | null;
  currency: 'zem' | 'point';
  saleStartAt: string | null;
  saleEndAt: string | null;
  isNew: boolean;
  isHot: boolean;
  owned: boolean;
}

export interface EquipmentUiSkinImage {
  url: string;
  version: number;
}

export interface EquipmentUiTextBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface EquipmentUiPadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface EquipmentUiSliceInsets {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface EquipmentUiTextConfig {
  textBox: EquipmentUiTextBox;
  padding: EquipmentUiPadding;
  fontSize: number;
  fontColor: string;
  fontWeight: string;
  textAlign: 'left' | 'center' | 'right';
  verticalAlign: 'top' | 'middle' | 'bottom';
  strokeColor: string | null;
  strokeWidth: number;
  shadowColor: string | null;
  shadowOffsetX: number;
  shadowOffsetY: number;
  shadowBlur: number;
}

export interface EquipmentUiSkinConfig {
  spriteImage: EquipmentUiSkinImage | null;
  bodyOffsetX: number;
  bodyOffsetY: number;
  tailImage: EquipmentUiSkinImage | null;
  tailOffsetX: number;
  tailOffsetY: number;
  sliceInsets?: EquipmentUiSliceInsets | null;
  textConfig: EquipmentUiTextConfig;
}

export interface AttackEquipmentItem extends BaseEquipmentItem {
  category: 'attack';
  attack: { type: number; param1: number; param2: number };
}

export interface EffectEquipmentItem extends BaseEquipmentItem {
  category: 'effect';
  idleBehavior?: IdleBehavior;
}

export interface RideEquipmentItem extends BaseEquipmentItem {
  category: 'ride';
  playerSpriteOverride: PlayerSpriteOverrideConfig;
  idleBehavior?: IdleBehavior;
}

/**
 * Motion parameters for a ground companion. The follower consumes the owner's
 * server-authoritative tile route rather than sending a second movement stream.
 * Values are deliberately expressed in tiles so every map uses the same
 * contract regardless of its pixel scale.
 */
export interface PetFollowBehavior {
  /** Number of owner route tiles kept between the owner and companion. */
  followDistanceTiles: number;
  /** Backlog at which the companion changes to its catch-up speed. */
  catchUpDistanceTiles: number;
  /** Multiplier applied to the owner's normal tile speed. */
  speedMultiplier: number;
  /** Multiplier used while catching up; must be >= speedMultiplier. */
  catchUpSpeedMultiplier: number;
}

export const DEFAULT_PET_FOLLOW_BEHAVIOR: Readonly<PetFollowBehavior> = {
  followDistanceTiles: 2,
  catchUpDistanceTiles: 4,
  speedMultiplier: 1.25,
  catchUpSpeedMultiplier: 2,
};

/**
 * Client-side safety envelope mirroring the catalog-admin validation. The
 * client still clamps because cached or hand-edited catalog payloads must not
 * turn a cosmetic follower into an unbounded movement loop.
 */
export const PET_FOLLOW_BEHAVIOR_LIMITS = {
  minFollowDistanceTiles: 1,
  maxFollowDistanceTiles: 8,
  maxCatchUpDistanceTiles: 16,
  maxSpeedMultiplier: 3,
  maxCatchUpSpeedMultiplier: 4,
} as const;

export interface PetEquipmentItem extends BaseEquipmentItem {
  category: 'pet';
  followBehavior: PetFollowBehavior;
  idleBehavior?: IdleBehavior;
}

export interface SpeechBubbleEquipmentItem extends BaseEquipmentItem {
  category: 'speech_bubble';
  uiSkinConfig: EquipmentUiSkinConfig;
}

export interface NicknameSkinEquipmentItem extends BaseEquipmentItem {
  category: 'nickname_skin';
  uiSkinConfig: EquipmentUiSkinConfig;
}

interface BaseIdleBehavior {
  periodMs: number;
}

export type IdleBehavior =
  | ({
      type: 'float';
      amplitudeY: number;
    } & BaseIdleBehavior)
  | ({
      type: 'sway';
      amplitudeX: number;
    } & BaseIdleBehavior)
  | ({
      type: 'pulse';
      scaleFrom?: number;
      scaleTo?: number;
      opacityFrom?: number;
      opacityTo?: number;
    } & BaseIdleBehavior)
  | ({
      type: 'wobble';
      rotationDeg: number;
    } & BaseIdleBehavior)
  | ({
      type: 'shake';
      amplitudeX?: number;
      amplitudeY?: number;
    } & BaseIdleBehavior)
  | ({
      type: 'drift';
      amplitudeX?: number;
      amplitudeY?: number;
    } & BaseIdleBehavior);

export type IdleBehaviorType = IdleBehavior['type'];

export type IdleBehaviorField =
  | 'amplitudeX'
  | 'amplitudeY'
  | 'periodMs'
  | 'scaleFrom'
  | 'scaleTo'
  | 'opacityFrom'
  | 'opacityTo'
  | 'rotationDeg';

interface IdleBehaviorPairRequirement {
  fields: readonly [IdleBehaviorField, IdleBehaviorField];
  minimum: 'positive' | 'nonNegative';
}

export interface IdleBehaviorDescriptor {
  fields: readonly IdleBehaviorField[];
  seed: Readonly<Partial<Record<IdleBehaviorField, number>>>;
  requiredPositive?: readonly IdleBehaviorField[];
  anyPositive?: readonly IdleBehaviorField[];
  oneOfPairs?: readonly IdleBehaviorPairRequirement[];
}

export const IDLE_BEHAVIOR_DESCRIPTORS = {
  float: {
    fields: ['amplitudeY', 'periodMs'],
    seed: { amplitudeY: 4, periodMs: 1200 },
    requiredPositive: ['amplitudeY', 'periodMs'],
  },
  sway: {
    fields: ['amplitudeX', 'periodMs'],
    seed: { amplitudeX: 4, periodMs: 1200 },
    requiredPositive: ['amplitudeX', 'periodMs'],
  },
  pulse: {
    fields: ['scaleFrom', 'scaleTo', 'opacityFrom', 'opacityTo', 'periodMs'],
    seed: { scaleFrom: 0.96, scaleTo: 1.04, periodMs: 1200 },
    requiredPositive: ['periodMs'],
    oneOfPairs: [
      { fields: ['scaleFrom', 'scaleTo'], minimum: 'positive' },
      { fields: ['opacityFrom', 'opacityTo'], minimum: 'nonNegative' },
    ],
  },
  wobble: {
    fields: ['rotationDeg', 'periodMs'],
    seed: { rotationDeg: 4, periodMs: 1200 },
    requiredPositive: ['rotationDeg', 'periodMs'],
  },
  shake: {
    fields: ['amplitudeX', 'amplitudeY', 'periodMs'],
    seed: { amplitudeX: 2, amplitudeY: 1, periodMs: 240 },
    requiredPositive: ['periodMs'],
    anyPositive: ['amplitudeX', 'amplitudeY'],
  },
  drift: {
    fields: ['amplitudeX', 'amplitudeY', 'periodMs'],
    seed: { amplitudeX: 4, amplitudeY: 3, periodMs: 1600 },
    requiredPositive: ['periodMs'],
    anyPositive: ['amplitudeX', 'amplitudeY'],
  },
} as const satisfies Record<IdleBehaviorType, IdleBehaviorDescriptor>;

export const IDLE_BEHAVIOR_TYPES = Object.keys(
  IDLE_BEHAVIOR_DESCRIPTORS,
) as IdleBehaviorType[];

export function getIdleBehaviorDescriptor(
  type: string,
): IdleBehaviorDescriptor | undefined {
  return IDLE_BEHAVIOR_DESCRIPTORS[type as IdleBehaviorType];
}

export type EquipmentItem =
  | AttackEquipmentItem
  | EffectEquipmentItem
  | RideEquipmentItem
  | PetEquipmentItem
  | SpeechBubbleEquipmentItem
  | NicknameSkinEquipmentItem;

export interface PlayerSpriteOverride {
  offsetX?: number;
  offsetY?: number;
  sitOffsetX?: number;
  sitOffsetY?: number;
  animation?: string;
}

export interface PlayerSpriteOverrideConfig {
  default: PlayerSpriteOverride;
  perDirection?: Partial<
    Record<EquipmentDirection, Partial<PlayerSpriteOverride>>
  >;
}

export interface EquipmentCatalog {
  attack: AttackEquipmentItem[];
  effect: EffectEquipmentItem[];
  ride: RideEquipmentItem[];
  pet?: PetEquipmentItem[];
  speech_bubble?: SpeechBubbleEquipmentItem[];
  nickname_skin?: NicknameSkinEquipmentItem[];
}
